import { useState, useEffect } from 'react';
import { pricingAPI } from '../../api/pricing';
import { Plus, Edit2, Trash2, X, Save, Check } from 'lucide-react';
import Loader from '../common/Loader';

export default function AdminPricing() {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'website',
    active: true,
    plans: [{
      name: '',
      price: 0,
      period: 'one-time',
      features: [''],
      popular: false,
      description: ''
    }]
  });

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      setLoading(true);
      const response = await pricingAPI.getAdminAll();
      setPricing(response.data || []);
    } catch (error) {
      console.error('Erreur:', error);
      setPricing([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await pricingAPI.update(editingId, formData);
      } else {
        await pricingAPI.create(formData);
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadPricing();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setFormData({
      title: item.title,
      description: item.description,
      type: item.type,
      active: item.active !== false,
      plans: item.plans.map(plan => ({
        name: plan.name,
        price: plan.price,
        period: plan.period || 'one-time',
        features: plan.features && plan.features.length > 0 ? [...plan.features] : [''],
        popular: plan.popular || false,
        description: plan.description || ''
      }))
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce tarif ?')) {
      try {
        setPricing(prev => prev.filter(item => item._id !== id));
        await pricingAPI.delete(id);
      } catch (error) {
        console.error('Erreur:', error);
        alert('Erreur lors de la suppression');
        loadPricing();
      }
    }
  };

  const toggleActive = async (id, currentActive) => {
    try {
      const updatedPricing = pricing.map(item => 
        item._id === id ? { ...item, active: !currentActive } : item
      );
      setPricing(updatedPricing);
      
      await pricingAPI.update(id, { active: !currentActive });
    } catch (error) {
      console.error('Erreur:', error);
      loadPricing();
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'website',
      active: true,
      plans: [{
        name: '',
        price: 0,
        period: 'one-time',
        features: [''],
        popular: false,
        description: ''
      }]
    });
  };

  const addPlan = () => {
    setFormData({
      ...formData,
      plans: [...formData.plans, {
        name: '',
        price: 0,
        period: 'one-time',
        features: [''],
        popular: false,
        description: ''
      }]
    });
  };

  const removePlan = (index) => {
    if (formData.plans.length > 1) {
      const newPlans = [...formData.plans];
      newPlans.splice(index, 1);
      setFormData({ ...formData, plans: newPlans });
    }
  };

  const updatePlan = (index, field, value) => {
    const newPlans = [...formData.plans];
    newPlans[index] = { ...newPlans[index], [field]: value };
    setFormData({ ...formData, plans: newPlans });
  };

  const addFeature = (planIndex) => {
    const newPlans = [...formData.plans];
    newPlans[planIndex].features.push('');
    setFormData({ ...formData, plans: newPlans });
  };

  const removeFeature = (planIndex, featureIndex) => {
    const newPlans = [...formData.plans];
    if (newPlans[planIndex].features.length > 1) {
      newPlans[planIndex].features.splice(featureIndex, 1);
      setFormData({ ...formData, plans: newPlans });
    }
  };

  const updateFeature = (planIndex, featureIndex, value) => {
    const newPlans = [...formData.plans];
    newPlans[planIndex].features[featureIndex] = value;
    setFormData({ ...formData, plans: newPlans });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader />
      </div>
    );
  }

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6 shadow-2xl">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 pb-6 border-b border-gray-700/50">
        <h2 className="text-2xl font-bold text-white">
          Gestion des Tarifs
        </h2>
        <button 
          onClick={() => setShowForm(true)} 
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20 active:scale-95 whitespace-nowrap"
        >
          <Plus size={20} />
          Nouveau Tarif
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 mt-60">
          <div className="bg-gray-900 border border-gray-700/50 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-700/50 sticky top-0 bg-gray-900 z-10">
              <h3 className="text-xl font-bold text-white">
                {editingId ? 'Modifier' : 'Nouveau'} Tarif
              </h3>
              <button 
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  resetForm();
                }} 
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors duration-200"
              >
                <X size={20} className="text-gray-300" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Titre *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    required
                  >
                    <option value="website">Site Web</option>
                    <option value="ecommerce">E-commerce</option>
                    <option value="marketing">Marketing</option>
                    <option value="print">Impression</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="hosting">Hébergement</option>
                  </select>
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    required
                    rows={3}
                    placeholder="Description générale de cette catégorie de tarifs"
                  />
                </div>
              </div>
              
              <div className="space-y-4 pt-4 border-t border-gray-700/50">
                <h3 className="text-lg font-bold text-white">Plans tarifaires</h3>
                
                {formData.plans.map((plan, planIndex) => (
                  <div key={planIndex} className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="text-md font-semibold text-white">Plan {planIndex + 1}</h4>
                      {formData.plans.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => removePlan(planIndex)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-all duration-200 text-sm"
                        >
                          <X size={14} />
                          Supprimer ce plan
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">
                          Nom du plan *
                        </label>
                        <input
                          type="text"
                          value={plan.name}
                          onChange={(e) => updatePlan(planIndex, 'name', e.target.value)}
                          className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                          required
                          placeholder="Ex: Starter, Pro, Enterprise"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">
                          Prix (€) *
                        </label>
                        <input
                          type="number"
                          value={plan.price}
                          onChange={(e) => updatePlan(planIndex, 'price', parseFloat(e.target.value) || 0)}
                          className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                          required
                          min="0"
                          step="0.01"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">
                          Période *
                        </label>
                        <select
                          value={plan.period}
                          onChange={(e) => updatePlan(planIndex, 'period', e.target.value)}
                          className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                          required
                        >
                          <option value="one-time">Forfait unique</option>
                          <option value="monthly">Mensuel</option>
                          <option value="yearly">Annuel</option>
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">
                          Description
                        </label>
                        <input
                          type="text"
                          value={plan.description}
                          onChange={(e) => updatePlan(planIndex, 'description', e.target.value)}
                          className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                          placeholder="Description courte du plan"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-300">
                        Fonctionnalités
                      </label>
                      <div className="space-y-2">
                        {plan.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={feature}
                              onChange={(e) => updateFeature(planIndex, featureIndex, e.target.value)}
                              className="flex-1 px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                              placeholder="Ex: 5 pages web, Responsive design, SEO de base"
                            />
                            {plan.features.length > 1 && (
                              <button 
                                type="button" 
                                onClick={() => removeFeature(planIndex, featureIndex)}
                                className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors duration-200"
                              >
                                <X size={16} />
                              </button>
                            )}
                          </div>
                        ))}
                        <button 
                          type="button" 
                          onClick={() => addFeature(planIndex)}
                          className="w-full px-4 py-2 border border-gray-600 hover:border-blue-400 text-gray-300 hover:text-blue-400 font-medium rounded-lg transition-all duration-300"
                        >
                          + Ajouter une fonctionnalité
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={plan.popular}
                          onChange={(e) => updatePlan(planIndex, 'popular', e.target.checked)}
                          className="w-4 h-4 text-blue-500 bg-gray-700 border-gray-600 rounded focus:ring-blue-500/50"
                        />
                        <span className="text-sm font-medium text-gray-300">
                          Plan populaire (mis en avant)
                        </span>
                      </label>
                    </div>
                  </div>
                ))}
                
                <button 
                  type="button" 
                  onClick={addPlan}
                  className="w-full px-4 py-3 border-2 border-dashed border-gray-600 hover:border-blue-400 text-gray-300 hover:text-blue-400 font-medium rounded-lg transition-all duration-300"
                >
                  + Ajouter un plan
                </button>
              </div>
              
              <div className="flex gap-3 pt-6 border-t border-gray-700/50">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    resetForm();
                  }} 
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 font-medium rounded-lg transition-all duration-300 active:scale-95"
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20 active:scale-95"
                >
                  <Save size={20} />
                  {editingId ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pricing.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p>Aucun tarif configuré. Cliquez sur "Nouveau Tarif" pour commencer.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {pricing.map((item) => (
            <div key={item._id} className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10">
              <div className="p-5 border-b border-gray-700/50">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-bold text-white">{item.title}</h3>
                    <span className="px-3 py-1 bg-gray-700/50 text-gray-300 text-sm rounded-full">
                      {item.type}
                    </span>
                    <button
                      onClick={() => toggleActive(item._id, item.active)}
                      className={`px-3 py-1 text-sm rounded-full ${item.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}
                    >
                      {item.active ? 'Actif' : 'Inactif'}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleEdit(item)} 
                      className="p-2 bg-gray-700/50 hover:bg-gray-600 text-gray-300 rounded-lg transition-all duration-200 hover:scale-105"
                      title="Modifier"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button 
                      onClick={() => handleDelete(item._id)} 
                      className="p-2 bg-gray-700/50 hover:bg-red-500/20 text-gray-300 hover:text-red-400 rounded-lg transition-all duration-200 hover:scale-105"
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                <p className="text-gray-400 mt-2">{item.description}</p>
              </div>
              
              <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {item.plans.map((plan, idx) => (
                    <div 
                      key={idx} 
                      className={`bg-gray-900/50 border rounded-xl p-5 relative ${plan.popular ? 'border-blue-500/50' : 'border-gray-700/50'}`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold rounded-full">
                          Populaire
                        </div>
                      )}
                      
                      <div className="mb-4">
                        <h4 className="text-lg font-bold text-white mb-2">{plan.name}</h4>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-white">{plan.price}€</span>
                          <span className="text-gray-400">
                            {plan.period === 'monthly' ? '/mois' : 
                             plan.period === 'yearly' ? '/an' : 
                             ' (forfait)'}
                          </span>
                        </div>
                        {plan.description && (
                          <p className="text-gray-400 text-sm mt-2">{plan.description}</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <h5 className="text-sm font-medium text-gray-300">Fonctionnalités:</h5>
                        <ul className="space-y-2">
                          {plan.features && plan.features.length > 0 ? (
                            plan.features.map((feature, fIdx) => (
                              <li key={fIdx} className="flex items-start gap-2">
                                <Check size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-300 text-sm">{feature}</span>
                              </li>
                            ))
                          ) : (
                            <li className="text-gray-500 text-sm">Aucune fonctionnalité</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}